import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import ProductTile from '../../components/ProductTile';
import { SEALED_GROUPS } from '../../lib/productTypes';

// WO-20: sealed products on the home page: newest releases (incl. pre-release) and the newest booster boxes / ETBs,
// with shortcuts into /sealed by kind. Renders nothing until sealed products exist, so the page is unchanged before.
const SHORTCUTS = ['booster-boxes', 'elite-trainer-boxes', 'booster-bundles', 'collections', 'tins', 'packs'];

function Row({ title, items, to }) {
  if (!items.length) return null;
  return (
    <div className="mb-10">
      <div className="mb-4 flex items-end justify-between gap-4">
        <h3 className="font-landing-display text-xl font-bold text-text-primary sm:text-2xl">{title}</h3>
        <Link to={to} className="inline-flex items-center gap-1 whitespace-nowrap text-sm text-brand-hi hover:text-text-primary">
          See all <ArrowRight size={14} />
        </Link>
      </div>
      <div className="-mx-4 flex snap-x gap-3 overflow-x-auto px-4 pb-2 sm:mx-0 sm:px-0 lg:grid lg:grid-cols-5 lg:overflow-visible">
        {items.map((p) => (
          <div key={p.product_id} className="w-44 shrink-0 snap-start lg:w-auto"><ProductTile product={p} compact /></div>
        ))}
      </div>
    </div>
  );
}

export default function SealedSection() {
  const apiUrl = process.env.REACT_APP_API_URL || '';
  const [latest, setLatest] = useState([]);
  const [boxes, setBoxes] = useState([]);

  useEffect(() => {
    const ctrl = new AbortController();
    const get = (q) => fetch(`${apiUrl}/api/products?game=pokemon&${q}`, { signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : { results: [] }))
      .then((d) => (Array.isArray(d?.results) ? d.results : []))
      .catch(() => []);
    Promise.all([
      get('sort=newest&limit=5'),
      get('sort=newest&limit=15&product_type=booster_box,elite_trainer_box'),
    ]).then(([a, b]) => {
      if (ctrl.signal.aborted) return;
      const seen = new Set(a.map((p) => p.product_id));
      setLatest(a);
      setBoxes(b.filter((p) => !seen.has(p.product_id)).slice(0, 5));
    });
    return () => ctrl.abort();
  }, [apiUrl]);

  if (!latest.length && !boxes.length) return null;
  return (
    <section className="relative border-t border-border-base bg-bg-app py-16 sm:py-20" data-testid="home-sealed">
      <div className="mx-auto max-w-[1300px] px-4 sm:px-6 lg:px-12">
        <div className="mb-10 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-brand-primary/20 bg-brand-primary/10 px-3 py-1 text-sm font-medium text-brand-hi">
              New: sealed products
            </div>
            <h2 className="font-landing-display text-3xl font-bold md:text-5xl">
              Booster boxes, ETBs and more, <span className="text-brand-hi">tracked too</span>
            </h2>
            <p className="mt-4 text-base text-text-secondary sm:text-lg">
              TCGplayer market prices, price history and live store and eBay listings for sealed product, next to the cards.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {SHORTCUTS.map((key) => {
              const g = SEALED_GROUPS.find((x) => x.key === key);
              return (
                <Link key={key} to={`/sealed?type=${key}`}
                      className="rounded-full border border-border-hi bg-bg-card px-3 py-1.5 text-sm text-text-secondary transition hover:border-brand-primary hover:text-text-primary">
                  {g.label}
                </Link>
              );
            })}
          </div>
        </div>
        <Row title="New & upcoming releases" items={latest} to="/sealed?sort=newest" />
        <Row title="Latest booster boxes & Elite Trainer Boxes" items={boxes} to="/sealed?type=booster-boxes" />
      </div>
    </section>
  );
}
